import { useState, useContext, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination, Mousewheel, Keyboard } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import SucursalesGrid from "./components/sucursalesGrid";
import MapaSucursales from "./components/MapaSucursales";
import SucursalModal from "./components/sucursalesModal";
import redesSociales from "./data/redes-sociales";
import { getSucursalesInfo } from "../../services/SucursalesServices";
import { preguntasFrecuentes, sobreNosotros } from "./data/faqs";
import { AuthContext } from "../../context/authContext";
import {
  useTheme, useMediaQuery, Box, Grid, Typography,
  TextField, Button, Paper, Snackbar, Alert,
} from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { postContacto } from "../../services/EmpresaServices";

// ─── Estilos de inputs ────────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    transition: "all 0.2s ease",
    "& fieldset": { border: "1.5px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "2px" },
    "&.Mui-focused": { backgroundColor: "#fff" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    "&.Mui-focused": { color: "#6366f1" },
  },
};

// ─── Section title ────────────────────────────────────────────────────────────
const SectionTitle = ({ icon, title, subtitle }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
    <Box sx={{
      width: 42, height: 42, borderRadius: "10px",
      bgcolor: "#eef2ff", display: "flex", alignItems: "center",
      justifyContent: "center", flexShrink: 0,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.1rem", md: "1.25rem" }, color: "#1e293b", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8", mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

// ─── Info card ────────────────────────────────────────────────────────────────
const InfoCard = ({ icon, title, subtitle, detail }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: "12px", border: "1.5px solid #e2e8f0",
    bgcolor: "#fff", height: "100%", display: "flex", alignItems: "flex-start", gap: 2,
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#a5b4fc", boxShadow: "0 4px 16px rgba(99,102,241,0.1)", transform: "translateY(-2px)" },
  }}>
    <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#1e293b", mb: 0.25 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", mb: 0.5 }}>{subtitle}</Typography>}
      <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#475569", wordBreak: "break-word" }}>{detail}</Typography>
    </Box>
  </Paper>
);

// ─── FAQ accordion ────────────────────────────────────────────────────────────
const FAQAccordion = ({ faqs, openIndex, onToggle }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    {faqs.map((faq, i) => {
      const isOpen = openIndex === i;
      return (
        <Paper key={i} elevation={0} sx={{
          borderRadius: "10px", overflow: "hidden",
          border: `1.5px solid ${isOpen ? "#a5b4fc" : "#e2e8f0"}`,
          bgcolor: isOpen ? "#eef2ff" : "#fff",
          transition: "all 0.2s ease",
        }}>
          <Box
            onClick={() => onToggle(i)}
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.75, cursor: "pointer" }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: isOpen ? "#6366f1" : "#1e293b", pr: 2 }}>
              {faq.pregunta}
            </Typography>
            {isOpen
              ? <ExpandLessIcon sx={{ color: "#6366f1", fontSize: 20, flexShrink: 0 }} />
              : <ExpandMoreIcon sx={{ color: "#94a3b8", fontSize: 20, flexShrink: 0 }} />
            }
          </Box>
          {isOpen && (
            <Box sx={{ px: 2.5, pb: 2 }}>
              <Typography sx={{ fontSize: "0.85rem", color: "#475569", lineHeight: 1.65 }}>
                {faq.respuesta}
              </Typography>
            </Box>
          )}
        </Paper>
      );
    })}
  </Box>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const Contacto = () => {
  const { empresa } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [openFAQIndex, setOpenFAQIndex] = useState(null);
  const [openNosotrosIndex, setOpenNosotrosIndex] = useState(null);
  const [redes, setRedes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", msg: "" });

  const [form, setForm] = useState({ nombre: "", email: "", asunto: "", mensaje: "" });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    if (empresa) {
      const redesGeneradas = redesSociales(empresa);
      setRedes(Array.isArray(redesGeneradas) ? redesGeneradas : []);
    }
    getSucursalesInfo()
      .then(data => setSucursales(Array.isArray(data) ? data : []))
      .catch(e => console.error("Error al obtener sucursales:", e));
  }, [empresa]);

  const sucursalesConGeo = sucursales.filter(
    s => s.latitud && s.longitud && !isNaN(s.latitud) && !isNaN(s.longitud)
  );

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await postContacto({
        ...form,
        destinatario: empresa?.email_sitio || "",
      });
      setSnackbar({ open: true, severity: "success", msg: "Mensaje enviado correctamente" });
      setForm({ nombre: "", email: "", asunto: "", mensaje: "" });
    } catch {
      setSnackbar({ open: true, severity: "error", msg: "Error al enviar el mensaje" });
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>

      {/* ── Hero ── */}
      <Box sx={{
        textAlign: "center", mb: { xs: 4, md: 5 },
        py: { xs: 4, md: 6 }, px: 2,
        borderRadius: "20px",
        background: "linear-gradient(135deg, #6366f1 0%, #818cf8 50%, #a78bfa 100%)",
        color: "#fff", position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", inset: 0, opacity: 0.06, backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.7rem", md: "2.4rem" }, letterSpacing: "-0.03em", mb: 1, position: "relative" }}>
          ¿Cómo podemos ayudarte?
        </Typography>
        <Typography sx={{ fontSize: { xs: "0.9rem", md: "1rem" }, opacity: 0.85, maxWidth: 480, mx: "auto", position: "relative" }}>
          Estamos para responder tus consultas. Encontrá toda la información que necesitás.
        </Typography>
      </Box>


      {/* ── Sucursales ── */}
      {sucursales.length > 0 && (
        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <SectionTitle
            icon={<StorefrontOutlinedIcon sx={{ color: "#6366f1", fontSize: 20 }} />}
            title="Nuestras Sucursales"
            subtitle={`${sucursales.length} sucursal${sucursales.length > 1 ? "es" : ""} disponible${sucursales.length > 1 ? "s" : ""}`}
          />
          <Paper elevation={0} sx={{ borderRadius: "16px", border: "1.5px solid #e2e8f0", overflow: "hidden" }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, minHeight: 350 }}>
              {sucursalesConGeo.length > 0 && (
                <Box sx={{ flex: 1.2, minHeight: { xs: 240, md: 350 } }}>
                  <MapaSucursales sucursales={sucursales} />
                </Box>
              )}
              <Box sx={{ flex: 1, p: 2.5, maxHeight: 350, overflowY: "auto", borderLeft: { md: "1.5px solid #e2e8f0" } }}>
                <SucursalesGrid sucursales={sucursales} openModal={s => setSelectedSucursal(s)} />
              </Box>
            </Box>
          </Paper>
          <SucursalModal sucursal={selectedSucursal} onClose={() => setSelectedSucursal(null)} />
        </Box>
      )}


      {/* ── Información de contacto ── */}
      <Box sx={{ mb: { xs: 4, md: 5 } }}>
        <SectionTitle
          icon={<PhoneOutlinedIcon sx={{ color: "#6366f1", fontSize: 20 }} />}
          title="Información de Contacto"
          subtitle="Todas las formas de comunicarte con nosotros"
        />
        <Grid container spacing={2}>
          {[
            { icon: <PhoneOutlinedIcon sx={{ color: "#6366f1", fontSize: 19 }} />, title: "Teléfono", subtitle: "LU-VI 9:00 a 18:00", detail: empresa?.telefonos_sitio || "+54 999 999999" },
            { icon: <EmailOutlinedIcon sx={{ color: "#6366f1", fontSize: 19 }} />, title: "Email", subtitle: "Respondemos en 24hs", detail: empresa?.email_sitio || "info@empresa.com" },
            { icon: <LocationOnOutlinedIcon sx={{ color: "#6366f1", fontSize: 19 }} />, title: "Dirección", subtitle: "Oficina central", detail: empresa?.domicilio_sitio || "Av. 9 de Julio 2050" },
            { icon: <AccessTimeOutlinedIcon sx={{ color: "#6366f1", fontSize: 19 }} />, title: "Horarios", subtitle: "Atención al público", detail: "LU-VI 9 a 18 | SA 9 a 13" },
          ].map((card, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <InfoCard {...card} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Formulario de contacto ── */}
      <Box sx={{ mb: { xs: 4, md: 5 } }}>
        <SectionTitle
          icon={<SendOutlinedIcon sx={{ color: "#6366f1", fontSize: 20 }} />}
          title="Envianos un mensaje"
          subtitle="Completá el formulario y te responderemos a la brevedad"
        />
        <Paper elevation={0} sx={{ borderRadius: "16px", border: "1.5px solid #e2e8f0", bgcolor: "#fff", p: { xs: 2.5, md: 4 } }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small" label="Nombre" name="nombre"
                  placeholder="Tu nombre" value={form.nombre}
                  onChange={handleChange} required sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small" label="Email" name="email" type="email"
                  placeholder="tu@correo.com" value={form.email}
                  onChange={handleChange} required sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Asunto" name="asunto"
                  placeholder="¿En qué podemos ayudarte?" value={form.asunto}
                  onChange={handleChange} required sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline rows={4} label="Mensaje" name="mensaje"
                  placeholder="Escribí tu consulta..." value={form.mensaje}
                  onChange={handleChange} required sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit" variant="contained" disabled={formLoading}
                  endIcon={<SendOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    borderRadius: "10px", textTransform: "none", fontWeight: 700,
                    fontSize: "0.9rem", px: 4, py: 1.25,
                    bgcolor: "#6366f1", boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
                    "&:hover": { bgcolor: "#4f46e5", transform: "translateY(-1px)" },
                    "&:disabled": { opacity: 0.6 },
                    transition: "all 0.18s ease",
                  }}
                >
                  {formLoading ? "Enviando..." : "Enviar mensaje"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>

      {/* ── FAQ + Sobre nosotros ── */}
      <Grid container spacing={3} sx={{ mb: { xs: 4, md: 5 } }}>
        <Grid item xs={12} md={6}>
          <SectionTitle
            icon={<HelpOutlineIcon sx={{ color: "#6366f1", fontSize: 20 }} />}
            title="Preguntas Frecuentes"
            subtitle="Las dudas más comunes"
          />
          <FAQAccordion
            faqs={preguntasFrecuentes}
            openIndex={openFAQIndex}
            onToggle={i => setOpenFAQIndex(openFAQIndex === i ? null : i)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTitle
            icon={<GroupsOutlinedIcon sx={{ color: "#6366f1", fontSize: 20 }} />}
            title="Sobre Nosotros"
            subtitle="Conocé nuestra historia"
          />
          <FAQAccordion
            faqs={sobreNosotros}
            openIndex={openNosotrosIndex}
            onToggle={i => setOpenNosotrosIndex(openNosotrosIndex === i ? null : i)}
          />
        </Grid>
      </Grid>


      {/* ── Redes sociales ── */}
      {redes.length > 0 && (
        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <SectionTitle
            icon={<ShareOutlinedIcon sx={{ color: "#6366f1", fontSize: 20 }} />}
            title="Seguinos en redes"
            subtitle="Enterate de novedades y ofertas"
          />

          {/* Solo visible en desktop */}
          {!isMobile && (
            <Box sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", maxWidth: 800, mx: "auto" }}>
              <Swiper
                loop={redes.length > 1}
                navigation
                pagination={{ clickable: true }}
                modules={[Autoplay, Navigation, Pagination, Mousewheel, Keyboard]}
                autoplay={redes.length > 1 ? { delay: 3500, disableOnInteraction: false } : undefined}
                style={{ height: "280px" }}
              >
                {redes.map((slide, idx) => {
                  const href = slide.url?.startsWith("http") ? slide.url : `https://${slide.url}`;
                  return (
                    <SwiperSlide key={idx}>
                      <a href={href} target="_blank" rel="noopener noreferrer"
                        style={{ display: "block", width: "100%", height: "100%", position: "relative" }}>
                        <Box component="img" src={slide.imagen} alt={slide.nombre || "red social"}
                          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        <Box sx={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(transparent 35%, rgba(0,0,0,0.65))",
                          display: "flex", alignItems: "flex-end", justifyContent: "center", pb: 3,
                        }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                            {slide.icono && (
                              <Box component="img" src={slide.icono} alt="" sx={{ width: 34, height: 34 }} />
                            )}
                            <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                              {slide.nombre}
                            </Typography>
                          </Box>
                        </Box>
                      </a>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </Box>
          )}
        </Box>
      )}

      {/* ── Snackbar feedback formulario ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ borderRadius: "10px" }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Contacto;